import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import PostForm from "./PostForm";
import { deletePost } from "../../services/api";

const PostEditModal = ({ post, onClose, onUpdated, onDeleted }) => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteButtonRef = useRef(null);


  useEffect(() => {
    const timer = setTimeout(() => {
      deleteButtonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 350);

    return () => clearTimeout(timer);
  }, []);

  const handleSuccess = (updatedPost) => {
    onUpdated(updatedPost);
    onClose();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deletePost(post.id);
      setShowSnackbar(true);
      onDeleted(post.id);
      setTimeout(() => {
        setShowSnackbar(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error al eliminar post", err);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        if (deleting) return;
        if (confirmDelete) {
          setConfirmDelete(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose, confirmDelete, deleting]);

  const modalContent = (
    <>
      <div
        className="modal-overlay"
        onClick={() => {
          if (!confirmDelete && !deleting) onClose();
        }}
      >
        <div
          className="modal-content"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cerrar modal */}
          <button
            type="button"
            onClick={onClose}
            disabled={confirmDelete || deleting}
            style={styles.cancelTop}
          >
            ✖️
          </button>

          {/* Título */}
          <h2 className="text-xl font-semibold mb-4 text-center">
            {t("post.edit_modal.title", "Modifier la publication")}
          </h2>

          {/* Formulario de edición */}
          <PostForm postToEdit={post} onSuccess={handleSuccess} />

          {/* Botón para solicitar la eliminación */}
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              ref={deleteButtonRef}
              onClick={() => setConfirmDelete(true)}
              className="rounded-xl border border-red-200 bg-red-50 px-5 py-3 font-semibold text-red-700 transition hover:bg-red-100"
            >
              {t("post.edit_modal.delete", "Eliminar publicación")}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmación independiente, siempre visible */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[20000] flex items-center justify-center bg-slate-950/70 px-4 py-6"
          onClick={() => {
            if (!deleting) setConfirmDelete(false);
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-post-title"
            aria-describedby="confirm-delete-post-message"
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="confirm-delete-post-title"
              className="mb-3 text-xl font-bold text-red-700"
            >
              {t("post.edit_modal.confirm_delete")}
            </h3>

            <p
              id="confirm-delete-post-message"
              className="mb-6 text-base leading-relaxed text-slate-700"
            >
              {t("post.edit_modal.confirm_message")}
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                {t("post.edit_modal.cancel")}
              </button>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-xl bg-red-600 px-5 py-3 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting
                  ? t("post.edit_modal.deleting")
                  : t("post.edit_modal.confirm_delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSnackbar && (
        <div
          style={{
            ...styles.snackbar,
            top: "20px",
            bottom: "auto",
          }}
        >
          {t("post.edit_modal.deleted_success")}
        </div>
      )}

      <style>{modalStyles}</style>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PostEditModal;

const styles = {
  delete: {
    background: "#e74c3c",
    color: "#fff",
    padding: "10px 16px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  cancel: {
    background: "#bdc3c7",
    color: "#fff",
    padding: "10px 16px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  cancelTop: {
    position: "absolute",
    top: "16px",
    right: "16px",
    background: "#eee",
    border: "none",
    borderRadius: "50%",
    padding: "8px",
    cursor: "pointer",
    fontSize: "16px",
  },
  confirmBox: {
    marginTop: "20px",
    background: "#fceaea",
    padding: "16px",
    borderRadius: "8px",
    textAlign: "center",
  },
  snackbar: {
    position: "fixed",
    bottom: "30px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#2ecc71",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: "8px",
    boxShadow: "0 0 12px rgba(0,0,0,0.25)",
    zIndex: 20000,
    fontWeight: "bold",
    animation: "fadeIn 0.3s ease-out",
  },
};

const modalStyles = `
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 80px;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

.modal-content {
  position: relative;
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 0 20px rgba(0,0,0,0.3);
  z-index: 10001;
  animation: slideUp 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0 }
  to { opacity: 1 }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0 }
  to { transform: translateY(0); opacity: 1 }
}
`;
